import { useState } from 'react'
import { useLanguage } from '../../i18n'
import type { Language } from '../../i18n'

interface DashboardHeaderProps {
  /** Optional fallback title used when no tenant identity is provided
   *  (e.g. admin views). When `tenantName` is set, the header renders
   *  the co-branded "TenantLogo · TenantName / Powered by Jarix" block
   *  instead of this title. */
  title?: string
  lastUpdate?: Date
  timeRange: '24h' | '7d' | '30d'
  onTimeRangeChange: (range: '24h' | '7d' | '30d') => void
  onExportPDF?: () => void
  onRefresh?: () => void
  isRefreshing?: boolean
  /** Tenant display name — when present, header switches to co-branded mode. */
  tenantName?: string
  /** Resolved tenant logo URL — `tenants.logo_url` override, or a Google
   *  Favicons URL derived from the tenant domain. May be undefined. */
  tenantLogoUrl?: string
}

export default function DashboardHeader({
  title,
  lastUpdate,
  timeRange,
  onTimeRangeChange,
  onExportPDF,
  onRefresh,
  isRefreshing = false,
  tenantName,
  tenantLogoUrl,
}: DashboardHeaderProps) {
  const { language, setLanguage, t } = useLanguage()
  const [logoError, setLogoError] = useState(false)

  const formatLastUpdate = () => {
    if (!lastUpdate) return language === 'es' ? 'Actualizado hace 5 min' : 'Updated 5 min ago'
    const diff = Math.floor((Date.now() - lastUpdate.getTime()) / 60000)
    if (diff < 1) return t.updatedNow
    if (diff < 60) {
      return language === 'es' ? `Actualizado hace ${diff} min` : `Updated ${diff} min ago`
    }
    return language === 'es' ? `Actualizado hace ${Math.floor(diff / 60)}h` : `Updated ${Math.floor(diff / 60)}h ago`
  }

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang)
  }

  return (
    <header className="dashboard-header">
      <div className="header-content">
        {tenantName ? (
          <div className="header-brand">
            {tenantLogoUrl && !logoError ? (
              <img
                /* key forces a fresh <img> mount whenever the URL
                   changes — defeats stuck onError state from a prior
                   failed load (e.g. expired LinkedIn signed URL). */
                key={tenantLogoUrl}
                src={tenantLogoUrl}
                alt=""
                className="header-tenant-logo"
                onError={() => setLogoError(true)}
                width={44}
                height={44}
                /* no-referrer keeps hotlink-protected CDNs (LinkedIn
                   media.licdn.com, some corporate logo CDNs) happy:
                   without a Referer header they tend to allow the
                   request, while sending icash-panel.jarix.io as the
                   referer triggers 403. */
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="header-tenant-initial" aria-hidden="true">
                {tenantName.charAt(0).toUpperCase()}
              </span>
            )}
            <div className="header-brand-text">
              <h1 className="header-tenant-name">{tenantName}</h1>
              <div className="header-brand-meta">
                <span className="header-powered-by">
                  <img
                    src="/branding/jarix-iso.png"
                    alt="Jarix"
                    width={14}
                    height={14}
                    className="header-jarix-iso"
                  />
                  {language === 'es' ? 'Operado por Jarix' : 'Powered by Jarix'}
                </span>
                <span className="header-meta-sep" aria-hidden="true">·</span>
                <span className="header-last-update">
                  <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                  </svg>
                  {formatLastUpdate()}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <h1 className="page-title">{title || t.securityOverview}</h1>
            <span className="last-update">
              <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
              </svg>
              {formatLastUpdate()}
            </span>
          </div>
        )}

        <div className="header-actions">
          {/* Language Selector */}
          <div className="language-selector">
            <button
              className={`lang-btn ${language === 'es' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('es')}
              title="Español"
            >
              ES
            </button>
            <button
              className={`lang-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('en')}
              title="English"
            >
              EN
            </button>
          </div>

          <div className="time-filter">
            <button
              className={`time-btn ${timeRange === '24h' ? 'active' : ''}`}
              onClick={() => onTimeRangeChange('24h')}
            >
              {t.last24h}
            </button>
            <button
              className={`time-btn ${timeRange === '7d' ? 'active' : ''}`}
              onClick={() => onTimeRangeChange('7d')}
            >
              {t.last7d}
            </button>
            <button
              className={`time-btn ${timeRange === '30d' ? 'active' : ''}`}
              onClick={() => onTimeRangeChange('30d')}
            >
              {t.last30d}
            </button>
          </div>

          <button className="btn btn-secondary" onClick={onExportPDF}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
            </svg>
            {t.exportPDF}
          </button>

          <button
            className={`btn btn-primary ${isRefreshing ? 'loading' : ''}`}
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            <svg
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              className={isRefreshing ? 'spin' : ''}
            >
              <path d="M23 4v6h-6M1 20v-6h6M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/>
            </svg>
            {isRefreshing ? (language === 'es' ? 'Actualizando...' : 'Refreshing...') : t.refresh}
          </button>
        </div>
      </div>
    </header>
  )
}
